// @flow
import React from 'react';
import { Form, FormikProps, Formik, FormikHelpers, FormikValues } from 'formik';
import * as Yup from 'yup';
import cogoToast from 'cogo-toast';
import api from 'apiClient/api';
import { CURRENT_COUNTRY_CODE } from 'config/env';
import { StringUtil } from 'utils/StringUtil';
import { FormButton } from 'components/molecules/formButton';
import { FormDropdown } from 'components/molecules/formDropdown';
import { FormInput } from 'components/molecules/formInput';
import { FormTextArea } from 'components/molecules/formTextArea';
import type { DropdownOption } from 'components/molecules/formDropdown';
import type { Vertical } from 'types/common';
import { GrPowerReset } from 'react-icons/gr';
import { COUNTRY_CODES, RESEND_OTP_ENDPOINT, SEND_OTP_ENDPOINT, VERIFY_OTP_ENDPOINT } from '../getInTouchForm/constants';
// eslint-disable-next-line
import styles from "./styles.scss";
import { getGclid } from 'utils/GetGclid';
import { NormalizeError } from 'utils/NormalizeError';
import { errorLog, infoLog } from 'loggers/logger';

// const API_ENDPOINT = 'sendEmail.json';
const API_ENDPOINT = 'postLead'; // For  Sales force API

type Props = {
  verticals: Array<Vertical>,
  verticalId: number | null,
  isMobile: boolean,
  showVerticalDropdown?: boolean,
  contactEmail?: string,
  callPostLeadAPI?: boolean,
  origin?: string,
  utmParameters: {
    UTMSource: string,
    UTMMedium: string,
    UTMCampaign: string,
    UTMDisplay: string,
    UTMTerm: string,
    UTMContent: string,
    CampaignCode: string,
    ProjectInterested: string,
  },
};

type FormState = {
  subject?: string,
  firstname?: string,
  lastname?: string,
  contactUs_gclid_field?: string,
  emailaddress?: string,
  mobilenumber?: string,
  remarks?: string,
  country?: string,
  DialingCode?: string,
  interestedIn?: string,
  otp?: string,
};

type State = {
  data: FormState,
};

const initialState: State = {
  data: {
    subject: '',
    firstname: '',
    lastname: '',
    contactUs_gclid_field: '',
    emailaddress: '',
    mobilenumber: '',
    remarks: '',
    country: CURRENT_COUNTRY_CODE,
    DialingCode: CURRENT_COUNTRY_CODE,
    interestedIn: '',
    otp: '',
  },
};

export class ContactUsForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      ...this.getInitialState(),
      selectedCountryName: 'IND',
      isSubmitted: false,
      showOTPInput: false,
      showLoader: false,
      counter: 0,
      timer: 30,
      resendCounter: 2,
      validatedNumber: false,
      minutes: 0,
      seconds: 0,
    };
    this.isThrottled = false;
  }

  getInitialState = () => {
    return {
      data: {
        ...initialState.data,
        subject: this.props.verticalId ? this.props.verticalId : -1,
      },
    };
  };


  stopSpinnerAnimation() {
    clearInterval(this.spinnerInterval);
    document.getElementById('mobilenumber').readOnly = false;
    !this.state.validatedNumber && this.state.counter !== 0 &&
      cogoToast.info(
        `${this.state.resendCounter === 0 ? 'No' : this.state.resendCounter} ${ this.state.resendCounter > 1 ? 'more attempts' : 'more attempt' }
         remaining to resend the otp`, { hideAfter: 2 });
      this.setState({
        showLoader: false,
        timer: 45 + this.state.counter * 15,
      });
  }

  startSpinnerAnimation() {
    this.spinnerInterval = setInterval(() => {
      if (this.state.showLoader) {
        if (this.state.timer === 0) this.stopSpinnerAnimation();
        else {
          this.setState(prevState => ({
            ...prevState,
            timer: prevState.timer > 0 ? prevState.timer - 1 : 0,
          }));
        }
      }
    }, 1000);
  }

  startTimer = () => {
    this.interval = setInterval(() => {
      const { minutes, seconds, timer } = this.state;
      if (seconds > 0) this.setState({ seconds: seconds - 1 });
      
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(this.interval);
          document.getElementById('mobilenumber').readOnly = false;
        } else {
          this.setState({
            seconds: 59,
            minutes: minutes - 1,
          });
        }
      }
    }, 1000);
  };

  throttle(func, delay) {
    return function() {
      if (!this.isThrottled) {
        func.apply(this, arguments);
        this.isThrottled = true;
        setTimeout(() => {
          this.isThrottled = false;
        }, delay);
      }
    };
  }

  throttledResendButtonClick = this.throttle(
    this.handleResendButtonClick,
    4000
  );

  handleResendButtonClick(mobileNumber, dialingCode, actions) {
    if (this.state.counter === 2) {
      document.getElementById('resend-button').classList.add('disable');
      cogoToast
        .info('You have reached the maximum limit for OTP requests.', { hideAfter: 2 })
        .then(() => {
          setTimeout(() => {window.location.reload()}, 2000);
        });
      return;
    }
    this.handleOtpResend(mobileNumber, dialingCode, actions);
  }

  handleOtpResend = (mobileNumber, dialingCode, actions) => {
    clearInterval(this.interval);
    let clearDialingCode = dialingCode.slice(1);
    document.getElementById('country').classList.add('disable');
    document.getElementById('mobilenumber').readOnly = true;
    this.setState(prevState => ({
      ...prevState,
      counter: prevState.counter + 1,
      resendCounter: prevState.resendCounter - 1,
    }));
    cogoToast.loading('Resending OTP... Please wait.', {hideAfter: 2});

    api
      .post(RESEND_OTP_ENDPOINT, { mobileNumber: `${mobileNumber}`, countryCode: clearDialingCode}) 
      .then(res => {
        if (res.data.status === 'success') {
          setTimeout(() => {
            cogoToast.success('OTP has been resent successfully!', {hideAfter: 2});
            this.setState({
              showLoader: true,
              minutes: 1,
              seconds: 59,
            });
            this.startSpinnerAnimation();
            this.startTimer();
          }, 3000);
        } 
      })
      .catch(error => {
        const errorMessage = error?.data?.message || 'Failed to resend OTP, please try after some time';
        if(errorMessage === 'Max limit reached for this otp verification' ) {
          cogoToast.info(errorMessage, {hideAfter: 2})
          .then(() => {
            setTimeout(() => {window.location.reload()}, 2000);
          });
        }else {
          setTimeout(() => {
            cogoToast.error(errorMessage, { hideAfter: 3 })
              .then(() => {
                 document.getElementById('mobilenumber').readOnly = false;
                 document.getElementById('country').classList.remove('disable');
                 this.setState(prevState => ({
                   ...prevState,
                   counter: Math.max(prevState.counter - 1, 0),
                   resendCounter: Math.min(prevState.resendCounter + 1, 2),
                 }));
               })
          }, 1000);
        }
      })
  };

  handleOtpSend = (mobileNumber, dialingCode, restValues, actions) => {
    clearInterval(this.interval);
    clearInterval(this.spinnerInterval);
    let clearDialingCode = dialingCode.slice(1);
    document.getElementById('mobilenumber').readOnly = true;
    cogoToast.loading('Sending OTP... Please wait.', {hideAfter: 1.3});
    infoLog(
      'REQUESTED_FOR_OTP',
      {
        details: restValues
      }
    );

    api
      .post(SEND_OTP_ENDPOINT, {mobileNumber: `${mobileNumber}`, countryCode: clearDialingCode})
      .then(res => {
        if (res.data.status === 'success') {
          infoLog(
            'OTP_SENT_SUCCESSFULLY',
            {
              api: SEND_OTP_ENDPOINT,
              payload: {mobileNumber: `${mobileNumber}`, countryCode: clearDialingCode},
              response: res.data
            }
          );
          setTimeout(() => {
            cogoToast.success('OTP sent successfully!', {hideAfter: 2});
            this.setState({
              showOTPInput: true,
              showLoader: true,
              minutes: 1,
              seconds: 59,
            });
            this.startSpinnerAnimation();
            this.startTimer();
          }, 3000);
        }
      })
      .catch(error => {
        const errorMessage = error?.data?.message || 'Failed to generate OTP, please try after some time';
        errorLog(
          'FAILED_TO_SEND_OTP',
          {
            api: SEND_OTP_ENDPOINT,
            payload: {mobileNumber: `${mobileNumber}`, countryCode: clearDialingCode},
            error: NormalizeError(error)
          }
        );
        setTimeout(() => {
          cogoToast.error(errorMessage, { hideAfter: 3 })
          .then(() => {
            document.getElementById('mobilenumber').readOnly = false;
            this.setState({ isSubmitted: false });
            actions.setSubmitting(false);
          });
        }, 1000);
      })
  };

  verifyOTP = (e, actions: FormikProps<FormState>, mobileNumber, dialingCode) => {
    let { minutes, seconds } = this.state;
    let clearDialingCode = dialingCode.slice(1);
    let otp = e.target.value.trim();
    actions.setFieldValue('otp', otp);
    if (otp.length === 4) {
      cogoToast.loading('Verifying OTP...', {hideAfter: 1});
      document.getElementById('otp').readOnly = true;

      if (minutes === 0 && seconds === 0) {
        cogoToast
          .error('OTP validation has timed out. Please resend OTP.', {hideAfter: 3})
          .then(() => {
            document.getElementById('otp').readOnly = false;
            actions.setFieldTouched({ otp: false });
          });
        return;
      }

      api
      .post(VERIFY_OTP_ENDPOINT, {mobileNumber: `${mobileNumber}`, countryCode: clearDialingCode, otp}) 
      .then(res => {
        if (res.data.status === 'success') {
          infoLog(
            'OTP_VERIFIED_SUCCESSFULLY',
            {
              api: VERIFY_OTP_ENDPOINT,
              payload: {mobileNumber: `${mobileNumber}`, countryCode: clearDialingCode, otp},
              response: res.data
            }
          );
          cogoToast.success('OTP Verified Successfully!', {hideAfter: 2})
          .then(() => {
            this.setState({
              showOTPInput: false,
              validatedNumber: true,
              minutes: 0,
              seconds: 0,
              isSubmitted: false
            });
            actions.setFieldValue('otp', '');
            actions.setSubmitting(false);
            document.getElementById('mobilenumber').readOnly = true;
            document.getElementById('country').classList.add('disable');
            clearInterval(this.interval);
            clearInterval(this.spinnerInterval);
          });
        }
      })
      .catch(error => {
        let errorMessage = error?.data?.message || 'Failed to verify otp, please try after some time';
        if(errorMessage === 'Max limit reached for this otp verification' ) {
          cogoToast.info(errorMessage, {hideAfter: 2})
          .then(() => {
            setTimeout(() => {window.location.reload()}, 2000);
          });
        }else {
          errorLog(
            'FAILED_TO_VERIFY_OTP',
            {
              api: VERIFY_OTP_ENDPOINT,
              payload: {mobileNumber: `${mobileNumber}`, countryCode: clearDialingCode, otp},
              error: NormalizeError(error)
            }
          );
          setTimeout(() => {
            cogoToast.error(errorMessage, { hideAfter: 3 })
            .then(() => {
              document.getElementById('otp').readOnly = false;
              actions.setFieldTouched({ otp: false });
            });
          }, 1000);
        }
      })
    }
  };

  resetFormState = (props, updatedValues = {}) => {
    const { DialingCode, mobilenumber, otp } = updatedValues;
    if (DialingCode !== undefined) props.values['DialingCode'] = DialingCode;
    if (mobilenumber !== undefined) props.values['mobilenumber'] = mobilenumber;
    if (otp !== undefined) props.values['otp'] = otp;
  
    this.setState({
      previousMobileNumber: mobilenumber || this.state.previousMobileNumber,
      showOTPInput: false,
      showLoader: false,
      counter: 0,
      timer: 30,
      resendCounter: 2,
      minutes: 0,
      seconds: 0,
      isSubmitted: false,
    });
  
    props.setSubmitting(false);
    document.getElementById('mobilenumber').readOnly = false;
    clearInterval(this.interval);
    clearInterval(this.spinnerInterval);
  };

  handleDropdownChange = (e, props) => {
    this.resetFormState(props, {
      DialingCode: e.value,
      mobilenumber: initialState.data.mobilenumber,
      otp: initialState.data.otp,
    });
  };

  handleMobileNumberChange = (e, props) => {
    const newValue = e.target.value;
    if (newValue !== this.state.previousMobileNumber) {
      this.resetFormState(props, { 
        DialingCode: props.values['DialingCode'], 
        mobilenumber: newValue, 
        otp: initialState.data.otp 
      });
    }
  };


  getSubmitButtonLabel = (props, validatedNumber) => {
    const { subject, country } = props.values;
    if (subject === -1 || subject === 3) {
      return country !== '+91' ? 'Submit' : validatedNumber ? 'Submit' : 'Verify Mobile Number';
    }
  
    return 'Submit';
  };

  getSubjectOptions = (): Array<DropdownOption> => {
    const { verticals } = this.props;

    return verticals.map((item: Vertical) => {
      return {
        value: item.id,
        label: item.sanitizedName,
      };
    });
  };

  render = () => {
    const { verticalId, isMobile, showVerticalDropdown = true } = this.props;
    const { minutes, seconds, timer, showLoader, showOTPInput, validatedNumber } = this.state;
    const options = this.getSubjectOptions();
    const buttonMode = isMobile ? 'transparent' : 'light';
    const country_code = COUNTRY_CODES;
    const interested_options = [
      {
        label: "I'm interested in a Total Environment Home",
        value: "I'm interested in a Total Environment Home",
      },
      {
        label: "I'm interested in doing business with Total Environment",
        value: "I'm interested in doing business with Total Environment",
      },
      {
        label: 'Others(please specify in the message section)',
        value: 'Others',
      },
    ];

    return (
      <div className={'form-container'}>
        <Formik
          initialValues={this.state.data}
          onSubmit={(values, actions) => {
            let { otp, ...restValues } = values;
            if ((values['subject'] === -1 || values['subject'] === 3) && values['country'] === '+91') {
              if (validatedNumber) {
                this.onSubmit(restValues, actions);
              } else {
                 document.getElementById('mobilenumber').readOnly = true;
                 this.handleOtpSend(
                   values['mobilenumber'],
                   values['DialingCode'],
                   restValues,
                   actions
                 );
                }
            } else {
                this.onSubmit(restValues, actions);
              }
          }}
          validationSchema={this.getSchema()}
        >
          {(props: FormikProps<State>) => (
            <Form>
              {!verticalId && showVerticalDropdown && (
                <FormDropdown
                  name={'subject'}
                  label={'How can we help?'}
                  options={options}
                  value={props.values['subject']}
                  showPlaceholder={false}
                />
              )}
              {/*{!isMobile && (*/}
              <FormInput
                name={'firstname'}
                label={'First Name'}
                placeholder={'First Name'}
                type={'text'}
                value={props.values['firstname']}
              />
              {/*)}*/}
              <FormInput
                name={'lastname'}
                label={'Last Name'}
                placeholder={'Last Name'}
                type={'text'}
                value={props.values['lastname']}
              />
               <FormInput
                id={'contactUs_gclid_field'}
                name={'contactUs_gclid_field'}
                type={'hidden'}
                value={props.values['contactUs_gclid_field']}
              />
              <FormInput
                name={'emailaddress'}
                label={'Email Address'}
                placeholder={'Email Address'}
                type={'email'}
                value={props.values['emailaddress']}
              />
              <div className={`contact-number-container ${validatedNumber ? 'validated' : ''}`}>
                <FormDropdown
                  name={'country'}
                  label={'Country'}
                  placeholder={''}
                  options={country_code}
                  value={props.values['country']}
                  selected_country={e =>
                    this.setState({ selectedCountryName: e })
                  }
                  onDropDownChange={e => this.handleDropdownChange(e, props)}
                  showPlaceholder={true}
                  disabled={validatedNumber}
                />
                <FormInput
                  name={'DialingCode'}
                  label={''}
                  placeholder={'CODE'}
                  type={'text'}
                  disabled={props.values['country'] !== ' '}
                  value={
                    props.values['country'] !== ' '
                      ? props.values['country']
                      : props.values['DialingCode']
                  }
                />
                <FormInput
                  name={'mobilenumber'}
                  label={'Mobile Number'}
                  placeholder={'Mobile Number'}
                  value={props.values['mobilenumber']}
                  type={'number'}
                  onChange={(e) => this.handleMobileNumberChange(e, props)}
                />
              </div>
              {showOTPInput && props.values['DialingCode'] === '+91' && (
                <div className={'otp-number-container'}>
                  <FormInput
                    id={'otp'}
                    name={'otp'}
                    label={'OTP Number'}
                    placeholder={'OTP Number'}
                    value={props.values['otp']}
                    type={'number'}
                    maxLength='4'
                    onChange={e => this.verifyOTP(e, props, props.values['mobilenumber'], props.values['DialingCode'])}
                  />
                  {seconds > 0 || minutes > 0 ? (
                    <span> Time Remaining: {minutes < 10 ? `0${minutes}` : minutes}: 
                    {seconds < 10 ? `0${seconds}` : seconds}</span>
                  ) : null}
                  <div
                    id="resend-button"
                    className={`button-container ${showLoader ? 'disable' : ''}`}
                    onClick={() =>
                      props.values['mobilenumber'].toString().length === 10 &&
                      this.throttledResendButtonClick(
                        props.values['mobilenumber'],
                        props.values['DialingCode'],
                        props
                      )
                    }
                  >
                    <GrPowerReset
                      className={`resend-icon ${showLoader ? 'loader' : ''}`}
                    />
                    {showLoader ? <p>Resend in {timer}s</p> : <p>Resend OTP</p>}
                  </div>
                </div>
              )}
              <FormDropdown
                name={'interestedIn'}
                placeholder={"I'm interested in"}
                options={interested_options}
                value={props.values['interestedIn']}
                showPlaceholder={true}
              />
              {/*{!isMobile && (*/}
              <FormTextArea
                name={'remarks'}
                label={'Message'}
                placeholder={'Message'}
                value={props.values['remarks']}
              />
              {/*)}*/}
              <FormButton
                text={this.getSubmitButtonLabel(props, validatedNumber)}
                disabled={props.isSubmitting}
                mode={buttonMode}
                onClick={() => this.setState({ isSubmitted: true })}
              />
            </Form>
          )}
        </Formik>
      </div>
    );
  };

  getValidation = () => {
    return Yup.string()
      .trim()
      .required('Please select an option');
  };

  // TODO: Common component for toast?
  onSubmit = (values: FormikValues, actions: FormikHelpers<FormikValues>) => {
    const trimmedValues = this.trimValues(values);
    const errors = this.getErrors(trimmedValues);
    const { callPostLeadAPI, origin, contactEmail: salesEmail } = this.props;
    const pageUrl = `${window.location.origin}${window.location.pathname}`;
    const {
      UTMSource,
      UTMMedium,
      UTMCampaign,
      UTMDisplay,
      UTMTerm,
      UTMContent,
      CampaignCode,
      ProjectInterested,
    } = this.props.utmParameters;
    const reference_no = this.props.referenceNumber;
    const rental_name = this.props.rentalName;
    const is_rental = !!this.props.isRental;
    if (errors.length > 0) {
      // TODO: show errors
      alert(errors);
      return;
    }

    this.state.isSubmitted && actions.setSubmitting(true);
    const { hide: hideLoader } = cogoToast.loading(
      'Submitting your information...',
      {
        hideAfter: 0,
      }
    );

    getGclid('contactUs');
    const contactGclidField = document.getElementById('contactUs_gclid_field')
    const payload = {
      ...trimmedValues,
      UTMSource,
      UTMMedium,
      UTMCampaign,
      UTMDisplay,
      UTMTerm,
      UTMContent,
      ProjectInterested,
      CampaignCode,
      reference_no,
      rental_name,
      is_rental,
    };
    payload.salesEmail = salesEmail;
    payload.isSalesForce = true;
    payload.Gclid = contactGclidField.value;
    if (!callPostLeadAPI) {
      payload.link = pageUrl;
      payload.isSalesForce = false;
    }
    api
      .post(API_ENDPOINT, payload)
      .then(res => {
        if (res.data.status === 'success') {
          hideLoader();
          infoLog(
            `CONTACT_US_SUCCESSFULL`,
            {
              api: API_ENDPOINT,
              payload,
              response: res.data
            }
          );
          setTimeout(() => {
            cogoToast.success('Submitted your information successfully!');
          }, 1000);
          window.location.href = '/thank-you';
          actions.resetForm({ ...this.getInitialState() });
        } else {
          hideLoader();
          setTimeout(() => {
            cogoToast.error(res.data.message);
          }, 1000);
        }
      })
      .catch(error => {
        errorLog(
            'CONTACT_US_FAILED',
            {
              api: API_ENDPOINT,
              payload: payload,
              error: NormalizeError(error)
            }
          );
        hideLoader();
        setTimeout(() => {
          cogoToast.error('Failed to submit your information');
        }, 1000);
      })
      .finally(() => {
        this.setState({ isSubmitted: false });
        actions.setSubmitting(false);
      });
  };

  trimValues = (values: FormState): FormState => {
    const trimmed = Object.keys(values).reduce((trimmed, key) => {
      if (!trimmed[key]) {
        delete trimmed.country;
        trimmed[key] = values[key].toString().trim();
      }
      trimmed.remarks = trimmed.interestedIn
        ? `${trimmed.interestedIn}\n${trimmed.remarks}`
        : trimmed.remarks;
      delete trimmed.interestedIn;
      return trimmed;
    }, {});

    delete trimmed.contactUs_gclid_field;

    return trimmed;
  };

  getErrors = (
    values: FormState
  ): Array<{ field: string, message: string }> => {
    const errors = [];
    if (!values['lastname'] || values['lastname'] === '') {
      errors.push({
        field: 'lastname',
        message: this.props.isMobile
          ? 'Please enter your First name'
          : 'Please enter your Last name',
      });
    }

    // For sales force api  Only mobile number is mandatory
    if (!values['mobilenumber'] || values['mobilenumber'] === '') {
      errors.push({
        field: 'emailaddress',
        message: 'Please enter your Email address or Phone number',
      });
    }
    return errors;
  };

  checkCountrySelected = () => {
    switch (this.state.selectedCountryName) {
      case 'IND':
      case 'U.S':
        return StringUtil.regexPatterns.phoneINDUS;
      case 'OM':
      case 'SG':
        return StringUtil.regexPatterns.phoneOMSG;
      case 'U.K':
        return StringUtil.regexPatterns.phoneUK;
      case 'AUS':
        return StringUtil.regexPatterns.phoneAUS;
      default:
        return StringUtil.regexPatterns.phone;
    }
  };

  getSchema = () => {
    const schema = {
      subject: Yup.string()
        .trim()
        .required('Please select an option'),
      firstname: Yup.string().trim(),
      lastname: Yup.string()
        .trim()
        .required('Please enter your Last name'),
      emailaddress: Yup.string()
        .trim()
        .email('Please enter a valid email')
        .required('Please enter your email'),
      mobilenumber: Yup.string()
        .trim()
        .required(
          this.props.isMobile
            ? 'Enter phone number'
            : 'Please enter your phone number'
        )
        .matches(
          this.checkCountrySelected(),
          this.props.isMobile
            ? 'invalid phone number'
            : 'enter valid phone number'
        ),

      DialingCode: Yup.string()
        .trim()
        .required('CODE')
        .matches(/^(\+?\d{1,4}|\d{1,4})$/, 'CODE'),
      remarks: Yup.string().trim(),
      interestedIn: this.state.isSubmitted
        ? this.getValidation()
        : Yup.string().trim(),
    };
    return Yup.object().shape(schema, [['mobilenumber', 'emailaddress']]);
  };
}
